import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useResendInvitationMutation } from 'api/main';
import { getRenderRoleName, getUserIdFromIri, parseDate, parseFullName } from 'utils/helpers';
import { RoleNames, userAvailabilityStatus } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import {
    MenuChangeRole,
    MenuDeactivate,
    MenuDeleteUser,
    MenuEditUser,
    MenuResendInvitation,
    MenuSendMessage,
    MenuSubstitute,
} from 'components/ActionsMenu/actions';
import { ActionsMenu, OnlineBadge, StyledTableRow } from 'components';
import { Typography, TableCell } from '@mui/material';

const DeleteUserItem = React.forwardRef((props, ref) => <MenuDeleteUser ref={ref} {...props} />);
const ImpersonateItem = React.forwardRef((props, ref) => <MenuSubstitute ref={ref} {...props} />);
const ChangeRoleItem = React.forwardRef((props, ref) => <MenuChangeRole ref={ref} {...props} />);
const EditUserItem = React.forwardRef((props, ref) => <MenuEditUser ref={ref} {...props} />);

const getUserStatus = status => {
    switch (status) {
        case userAvailabilityStatus.ACTIVE:
            return texts.ACTIVATED;
        case userAvailabilityStatus.INACTIVE:
            return texts.DEACTIVATED;
        case userAvailabilityStatus.PENDING_PASSWORD_CHANGE:
            return texts.SENT;
        default:
            return texts.DEACTIVATED;
    }
};

const DistributorUserRow = ({ user, internalUser, userIsServicer }) => {
    const isAccountNotConfirmed = useMemo(
        () => user?.availabilityStatus === userAvailabilityStatus.PENDING_PASSWORD_CHANGE,
        [user]
    );

    const role = user?.subscriptions?.[0]?.role;

    const [resendInvitation, { isLoading: resendLoader }] = useResendInvitationMutation();

    const impersonatedByUserId = useMemo(
        () =>
            internalUser?.impersonatedByUser
                ? getUserIdFromIri(internalUser.impersonatedByUser)
                : null,
        [internalUser]
    );

    const handleResendInvitation = useCallback(() => {
        resendInvitation(user.email);
    }, [user, resendInvitation]);

    const isMainUser = user.distributorCompany !== null;

    const canChangeRole = useMemo(
        () =>
            [RoleNames.PSI, RoleNames.ADMIN_CIF].includes(
                internalUser?.subscriptions?.[0]?.role?.name
            ) &&
            user.id !== internalUser?.id &&
            !isMainUser,
        [internalUser, isMainUser, user.id]
    );

    return (
        <StyledTableRow key={user.id}>
            <TableCell>
                <OnlineBadge online={user?.online} />
            </TableCell>
            <TableCell>{parseFullName(user)}</TableCell>
            {userIsServicer && (
                <TableCell>
                    {user.subscriptions[0].distributorCompany?.corporateName ||
                        user.distributorCompany?.corporateName}
                </TableCell>
            )}
            <TableCell>{user?.email}</TableCell>
            <TableCell>{user.phoneNumber || '-'}</TableCell>
            <TableCell>
                <Typography>{getRenderRoleName(role)}</Typography>
                <Typography>{isMainUser ? texts.DISTRIBUTOR_MAIN_USER : ''}</Typography>
            </TableCell>
            <TableCell>{user?.tag || '-'}</TableCell>
            <TableCell>{getUserStatus(user?.availabilityStatus)}</TableCell>
            <TableCell>{parseDate(user?.lastLoggedAt) || '-'}</TableCell>
            <TableCell>
                {userIsServicer ? (
                    <ActionsMenu>
                        <DeleteUserItem id={user.id} />
                        {isMainUser && (
                            <ImpersonateItem
                                user={user}
                                disabled={!user.emailConfirmed}
                                originalPath="/distributor-users"
                                itemText={texts.IMPERSONATE_DISTRIBUTOR}
                            />
                        )}
                        <MenuSendMessage receiver={user} />
                        <MenuDeactivate
                            id={user.id}
                            impersonatedByUserId={impersonatedByUserId}
                            currentUserId={internalUser.id}
                            status={user.availabilityStatus}
                        />
                        <EditUserItem user={user} />
                    </ActionsMenu>
                ) : (
                    <ActionsMenu>
                        <DeleteUserItem id={user.id} />
                        <ImpersonateItem
                            user={user}
                            disabled={!user.emailConfirmed}
                            originalPath="/distributor-users"
                            itemText={texts.IMPERSONATE_DISTRIBUTOR}
                        />
                        <MenuSendMessage receiver={user} />
                        <MenuDeactivate
                            id={user.id}
                            impersonatedByUserId={impersonatedByUserId}
                            currentUserId={internalUser.id}
                            status={user.availabilityStatus}
                        />
                        {isAccountNotConfirmed && (
                            <MenuResendInvitation
                                onClick={handleResendInvitation}
                                loading={resendLoader}
                                label={texts.RESEND_INVITATION}
                            />
                        )}
                        {canChangeRole && <ChangeRoleItem user={user} />}
                    </ActionsMenu>
                )}
            </TableCell>
        </StyledTableRow>
    );
};

DistributorUserRow.propTypes = {
    user: PropTypes.object,
    internalUser: PropTypes.object,
    userIsServicer: PropTypes.bool,
};

export default DistributorUserRow;
